// Maps the current route to the help section id so the Layout topbar can
// render a "Help on this page" button that opens /help?s=<id> in a new tab.
//
// Order matters — earliest match wins, so put more-specific routes first.
// Return `null` to hide the help button on that page (e.g. the help page itself).

const ROUTE_TO_HELP = [
  // Help itself — don't show the button.
  [/^\/help$/,                          null],

  // Auth + marketing — no help button on these.
  [/^\/login/,                          null],
  [/^\/register/,                       null],
  [/^\/verify-email/,                   null],
  [/^\/$/,                              null],
  [/^\/auth\//,                         null],

  // Core pages
  [/^\/dashboard$/,                     'home'],
  [/^\/quickstart/,                     'quickstart'],
  [/^\/dashboards\/view/,               'dashboard-view'],
  [/^\/dashboards/,                     'reports'],
  [/^\/insights/,                       'reports'],
  [/^\/reports\/[^/]+\/(edit|builder)/, 'report-builder'],
  [/^\/reports\/[^/]+/,                 'dashboard-view'],
  [/^\/reports/,                        'reports'],
  [/^\/data-sources/,                   'data-sources'],
  [/^\/sources/,                        'data-sources'],
  [/^\/data-prep/,                      'data-prep'],
  [/^\/glossary/,                       'glossary'],
  [/^\/alerts/,                         'alerts'],
  [/^\/scheduling/,                     'schedules'],
  [/^\/schedules/,                      'schedules'],
  [/^\/anomaly-scans/,                  'anomaly-scans'],
  [/^\/discussions/,                    'discussions'],
  [/^\/access-control/,                 'access-control'],
  [/^\/activity/,                       'activity'],
  [/^\/settings/,                       'settings'],
  [/^\/kpi-monitor/,                    'kpi-monitor'],
  [/^\/lineage/,                        'lineage'],
  [/^\/embed-manager/,                  'embed-manager'],
  [/^\/visual-library\/edit/,           'ai-visual'],
  [/^\/visual-library/,                 'visual-library'],
  [/^\/explore/,                        'explore-ai'],
  [/^\/ai-copilot/,                     'explore-ai'],
  [/^\/semantic-models/,                'semantic-models'],
  [/^\/tenant-admin/,                   'tenant-admin'],

  // Analyze
  [/^\/investigations\/diff/,           'investigations'],
  [/^\/investigations/,                 'investigations'],
  [/^\/executive-summaries/,            'executive-summaries'],
  [/^\/morning-brief/,                  'ai-insights'],
  [/^\/ai-insights/,                    'ai-insights'],

  // Decisions
  [/^\/my-work/,                        'my-work'],
  [/^\/initiatives/,                    'initiatives'],
  [/^\/objectives/,                     'objectives'],
  [/^\/results/,                        'results'],
  [/^\/decision-policies/,              'decision-policies'],
  [/^\/recommendations/,                'recommendation-rules'],

  // Governance
  [/^\/governance/,                     'governance'],
  [/^\/lineage-explorer/,               'lineage'],
  [/^\/ai-coverage/,                    'ai-coverage'],
  [/^\/change-requests/,                'change-requests'],
  [/^\/change-lists/,                   'change-requests'],
  [/^\/coach-studio/,                   'coach-studio'],

  // Collaborate
  [/^\/threads/,                        'discussions'],
  [/^\/notifications/,                  'notifications'],

  // Apps
  [/^\/apps/,                           'apps-workspaces'],
  [/^\/marketplace/,                    'marketplace'],

  // Developer
  [/^\/sdk\/visuals/,                   'sdk-visuals'],
  [/^\/sdk\/connectors/,                'sdk-connectors'],
  [/^\/developers/,                     'mgmt-api'],

  // Hubs + billing
  [/^\/administration/,                 'administration'],
  [/^\/platform-console/,               'platform-console'],
  [/^\/billing/,                        'billing'],

  // Trusted-AI — Sentinels, incidents, packs, drift & scenarios
  [/^\/admin\/sentinel-packs/,          'sentinel-packs'],
  [/^\/admin\/sentinels/,               'sentinels'],
  [/^\/admin\/incidents/,               'incidents'],
  [/^\/admin\/drift-alerts/,            'drift-alerts'],
  [/^\/admin\/scenario-simulator/,      'scenario-simulator'],
  [/^\/admin\/risk-signals/,            'risk-signals'],
  [/^\/admin\/recommendation-approvals/,'recommendation-rules'],
  [/^\/admin\/recommendations/,         'recommendation-rules'],

  // Governance admin tools
  [/^\/admin\/adoption-analytics/,      'adoption-analytics'],
  [/^\/admin\/certification-queue/,     'certification-queue'],
  [/^\/admin\/metric-registry/,         'metric-registry'],
  [/^\/admin\/calculation-packs/,       'calculation-packs'],
  [/^\/admin\/data-quality/,            'data-quality'],
  [/^\/admin\/catalog/,                 'data-catalog'],
  [/^\/admin\/explore-eval/,            'explore-eval'],
  [/^\/admin\/pii-compliance/,          'pii-compliance'],
  [/^\/admin\/explore-kpis/,            'governance-analytics'],
  [/^\/admin\/retention-preview/,       'retention'],
  [/^\/admin\/what-if-analysis/,        'scenario-simulator'],
  [/^\/admin\/investigations-analytics/,'investigations'],
  [/^\/admin\/predictive-models/,       'ai-agents'],

  // People & Access
  [/^\/admin\/users/,                   'users'],
  [/^\/admin\/roles/,                   'access-control'],
  [/^\/admin\/sso/,                     'sso'],
  [/^\/admin\/audit/,                   'audit-log'],
  [/^\/admin\/api-keys/,                'api-keys'],

  // AI Configuration
  [/^\/admin\/ai-agents/,               'ai-agents'],
  [/^\/admin\/voice/,                   'voice'],
  [/^\/admin\/ai-traces/,               'ai-traces'],
  [/^\/admin\/ai-usage/,                'ai-usage'],

  // Workspace Settings
  [/^\/admin\/branding/,                'branding'],
  [/^\/admin\/version-control/,         'version-control'],
  [/^\/admin\/git-backup/,              'version-control'],
  [/^\/admin\/feedback/,                'feedback-admin'],
  [/^\/admin\/doc-health/,              'doc-analytics'],
  [/^\/admin\/pyramid-mappings/,        'pyramid-mapping'],
  [/^\/admin\/feature-flags/,           'workspace-flags'],

  // Platform engineering / operations
  [/^\/admin\/storage/,                 'object-storage'],
  [/^\/admin\/health/,                  'platform-health'],
  [/^\/admin\/system-updates/,          'system-updates'],
  [/^\/admin\/site-root/,               'site-root'],
  [/^\/admin\/revenue/,                 'revenue'],
  [/^\/admin\/platform-billing/,        'billing'],
  [/^\/admin\/demo-env/,                'admin-console'],
  [/^\/admin\/community/,               'admin-console'],

  // Super-admin pages
  [/^\/admin\/llm/,                     'llm-providers'],
  [/^\/admin\/tenants/,                 'tenant-lifecycle'],
  [/^\/admin\/deploy-status/,           'deploy-status'],
  [/^\/admin\/all-schedules/,           'all-schedules'],
  [/^\/admin\/signup-requests/,         'signup-requests'],
  [/^\/admin\/signup-invites/,          'signup-invites'],
  [/^\/admin\/jobs/,                    'background-jobs'],
  [/^\/admin\/trial-leads/,             'trial-leads'],
  [/^\/admin\/inbound-emails/,          'inbound-emails'],
  [/^\/admin$/,                         'admin-console'],
  [/^\/system-logs/,                    'system-logs'],
];

/**
 * Resolve a help-section id for the given pathname, or `null` if no help
 * button should render for that route.
 */
export function resolveHelpSection(pathname) {
  for (const [re, id] of ROUTE_TO_HELP) {
    if (re.test(pathname)) return id;
  }
  // Default — point at the top of the docs so people still have somewhere to go.
  return 'home';
}


/**
 * Map the contextual `?` HelpIcon docKeys to a Help section + optional
 * heading anchor (help-h-<slug>) so the icons deep-link into the right place.
 */
export const DOC_KEY_MAP = {
  'aggregates':           { section: 'formulas',        heading: 'aggregation-types' },
  'aggregates-filters':   { section: 'scoped-measures', heading: 'scoped-measures-set-algebra' },
  'calculated-measures':  { section: 'formulas',        heading: 'creating-a-calculated-measure' },
  'calculated-dimensions': { section: 'semantic-models', heading: null },
  'hierarchies':          { section: 'semantic-models', heading: null },
  'glossary':             { section: 'glossary',        heading: null },
};

export function resolveDocKey(docKey) {
  return DOC_KEY_MAP[docKey] || { section: 'semantic-models', heading: null };
}
